import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { LabourRateFormDialog } from './LabourRateFormDialog';
import { labourRatesApi, type LabourRate, type CreateLabourRateInput } from '../../api/labourRates.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function LabourRateListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LabourRate | null>(null);
  const [deleting, setDeleting] = useState<LabourRate | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['labour-rates', page, limit, search],
    queryFn: () => labourRatesApi.list({ page, limit, search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['labour-rates'] });

  const createMut = useMutation({
    mutationFn: (input: CreateLabourRateInput) => labourRatesApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateLabourRateInput }) => labourRatesApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => labourRatesApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<LabourRate>[] = [
    { key: 'activityCode', label: 'Code', width: 110 },
    { key: 'activityName', label: 'Activity' },
    { key: 'unit', label: 'Unit' },
    { key: 'standardRate', label: 'Rate (₹)', render: (r) => r.standardRate.toFixed(2) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader
        title="Labour Rate Master"
        action={hasPermission('labour_rate.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Labour Rate</Button>}
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search activity / code" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('labour_rate.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('labour_rate.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <LabourRateFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete labour rate"
        message={`Soft-delete ${deleting?.activityName}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
